<template>
  <b-overlay :show="loading" class="animated">
    <v-server-table
      :columns="table_config.columns"
      :options="table_config.options"
      :theme="theme"
      :ref="ref"
      id="schedule-server-table"
      class="w-100"
      @row-click="rowClick"
    >
      <div v-if="show_buttons" slot="beforeTable">
        <div class="float-right pb-3">
          <div class="d-inline-block">
            <b-overlay :show="approving" spinner-small>
              <b-button
                variant="success"
                @click="approveAll"
                :disabled="!collection.length"
              >
                <i class="fa fa-check-circle-o" /> Approve Drafted
              </b-button>
            </b-overlay>
          </div>
          <div class="d-inline-block pl-2">
            <b-overlay :show="declining" spinner-small>
              <b-button
                variant="danger"
                @click="declineAll"
                :disabled="!collection.length"
              >
                <i class="fa fa-times-circle-o" /> Decline Drafted
              </b-button>
            </b-overlay>
          </div>
        </div>
      </div>
      <span slot="date" slot-scope="{ row }">
        <router-link :to="`/schedules/${row.id}`">
          <b>{{ row.date | moment("LL") }}</b>
        </router-link>
      </span>
      <span slot="status" slot-scope="{ row }">
        <existence-gate
          :entity="row"
          :levels="table_data.status"
          :field="'status'"
        >
          <template #existing-content>
            <b-badge
              v-if="
                getData(row, { levels: table_data.status, field: 'status' }) ===
                'Draft'
              "
              variant="warning"
              pill
              >Draft</b-badge
            >
            <b-badge
              v-if="
                getData(row, { levels: table_data.status, field: 'status' }) ===
                'Declined'
              "
              variant="danger"
              pill
              >Declined</b-badge
            >
            <b-badge
              v-if="
                getData(row, { levels: table_data.status, field: 'status' }) ===
                'Approved'
              "
              variant="success"
              pill
              >Approved</b-badge
            >
          </template>
        </existence-gate>
      </span>
      <span slot="type" slot-scope="{ row }">
        {{ getProcessesType(row) }}
      </span>
      <span slot="sampletype" slot-scope="{ row }">
        {{ getData(row, { levels: table_data.sample_type, field: "name" }) }}
      </span>
      <span slot="samples" slot-scope="{ row }"> {{row.samples_count}} </span>
      <span slot="process" slot-scope="{ row }">
        {{
          row.getProcessSchedules().models.length
            ? getProcessesNames(row)
            : null
        }}
      </span>
      <span slot="staff" slot-scope="{ row }">
        <router-link
          v-if="row.staff"
          :to="`/customers/${getCreatorData(row, 'id')}`"
        >
          <b>{{ getCreatorData(row, "name") }}</b>
        </router-link>
      </span>
    </v-server-table>
  </b-overlay>
</template>

<script>
import Vue from "vue";
import InstrumentCapacityBar from "../instrument/InstrumentCapacityBar";
import BaseServerTable from "@/components/shared/BaseServerTable";
import ExistenceGate from "@/components/shared/ExistenceGate";
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
import InstrumentInstanceCollection from "@/collections/lab/InstrumentInstanceCollection";
import ScheduleCollection from "@/collections/lab/ScheduleCollection";
import ScheduleCapacityBar from "./ScheduleCapacityBar";
import { ServerTable } from "vue-tables-2";
import { getEntityData } from "@/shared/utils";

Vue.use(ServerTable);

export default {
  name: "ScheduleServerTable",
  extends: BaseServerTable,
  components: {
    ExistenceGate,
    InstrumentCapacityBar,
    ScheduleCapacityBar,
    ServerTable,
  },
  mixins: [ToastNotifications],
  props: {
    collection: {
      type: Object,
      default: () => new ScheduleCollection(),
    },
    collection_status: {
      type: String,
      default: null,
    },
    collection_type: {
      type: String,
      default: null,
    },
    show_buttons: {
      type: Boolean,
      default: false,
    },
    show_columns: {
      type: Array,
      default: () => [
        // "date",
        "status",
        "type",
        "sampletype",
        "samples",
        "process",
        "staff",
        "note",
      ],
    },
    filterable: {
      type: Array,
      default: () => ["searchable"],
    },
    sortable: {
      type: Array,
      default: () => ["date"],
    },
    filter_date: {
      type: Object,
      default: () => null,
    },
    route_override: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      table_config: Object.assign(this.getDefaultTableConfig(), {
        options: {
          headings: {
            sampletype: "Sample Type",
            samples: "Samples",
            staff: "Author",
          },
          filterable: this.filterable,
          sortable: this.sortable,
        },
      }),
      approving: false,
      declining: false,
      date_format: "YYYY-MM-DD",
      table_data: {
        status: ["current_schedule_status_log", "schedule_status"],
        sample_type: ["sample_type"],
      },
    };
  },
  methods: {
    getData({ _reference }, data) {
      return getEntityData({
        entity: _reference,
        ...data,
      });
    },
    getScheduleInstruments(process) {
      return process.instrument_instances.reduce((acc, cur) => {
        acc.add(cur);

        return acc;
      }, new InstrumentInstanceCollection());
    },
    getProcessesType(schedule) {
      if (schedule.getProcessSchedules().models.length) {
        let type =
          schedule.getProcessSchedules().models[0].schedulable_process_type;
        return type.includes("Prep") ? "Prep" : "Test";
      }
      return;
    },
    getProcessesNames(schedule) {
      return schedule
        .getProcessSchedules()
        .models.map((process) =>
          process.schedulable_process
            ? process.schedulable_process.name
            : "Process unavailable"
        )
        .join(", ");
    },
    getSampleData(schedule) {
      return schedule
        .getProcessSchedules()
        .models.map((process) =>
          process.schedulable_process
            ? `${process.schedulable_process.name}: ${
                process.getSamples().models.length
              } samples`
            : "Process unavailable"
        );
    },
    getCreatorData(schedule, data) {
      return schedule.getStaff()[data];
    },
    getStatusVariant(status) {
      return status.getVariant();
    },
    prepareCollectionFilter(filter) {
      const connections = [
        "sampleType",
        //"staff",
        "processSchedules.schedulableProcess",
        "currentScheduleStatusLog.scheduleStatus",
      ];

      connections.forEach((connection) => {
        filter.connectionAdd(connection);
      });

      filter.sortAddAsc("status", true);

      this.filter_date &&
        filter.filterEquals("date", this.filter_date.format("YYYY-MM-DD"));
      this.collection_type &&
        filter.filterContains(
          "processSchedules.schedulable_process_type",
          this.collection_type
        );
      this.collection_status &&
        filter.filterEquals(
          "currentScheduleStatusLog.scheduleStatus.status",
          this.collection_status
        );

      filter.filterEquals("laboratory_id", this.$store.getters.getLaboratoryContext.id);
      return filter;
    },
    refresh() {
      this.$refs[this.ref].refresh();
    },
    approveAll() {
      this.approving = true;
      let requests = [];

      this.collection.each((model) => {
        if (
          model.getCurrentScheduleStatusLog().getScheduleStatus().status ===
          "Draft"
        ) {
          requests = [...requests, model.setStatus("Approved")];
        }
      });

      Promise.all(requests).then(
        () => {
          this.quickSuccessToast(
            `${this.filter_date.format("L")} Schedules Approved`,
            `"All the drafted schedules for ${this.filter_date.format(
              "L"
            )} have been approved.`
          );

          this.$emit("updateTables");

          this.approving = false;
        },
        () => {
          this.quickWarningToast(
            `${this.filter_date.format("L")} Approved Failed`,
            `The schedules for ${this.filter_date.format(
              "L"
            )} could not be approved.`
          );

          this.approving = false;
        }
      );
    },
    declineAll() {
      this.declining = true;
      let requests = [];

      this.collection.each((model) => {
        if (
          model.getCurrentScheduleStatusLog().getScheduleStatus().status ===
          "Draft"
        ) {
          requests = [...requests, model.setStatus("Declined")];
        }
      });

      Promise.all(requests).then(
        () => {
          this.quickSuccessToast(
            `${this.filter_date.format("L")} Schedules Declined`,
            `All the drafted schedules for ${this.filter_date.format(
              "L"
            )} have been declined.`
          );

          this.$emit("updateTables");

          this.declining = false;
        },
        () => {
          this.quickWarningToast(
            `${this.filter_date.format("L")} Declined Failed`,
            `The schedules for ${this.filter_date.format(
              "L"
            )} could not be declined.`
          );
          this.declining = false;
        }
      );
    },
    rowClick({ row }) {
      if (this.route_override) {
        this.$emit("pushRoute", row.id)
        return;
      }
      this.$router.push(`/schedules/${row.id}`);
    },
  },
  watch: {
    filter_date() {
      this.collection
        .getFilter()
        .filterEquals("date", this.filter_date.format("YYYY-MM-DD"));
      this.refresh();
    },
    "collection.models.length"(value) {
      if (this.collection_status && this.collection_type) {
        this.$emit("updateCounter", {
          status: this.collection_status.toLowerCase(),
          type: this.collection_type.toLowerCase(),
          value,
        });
      }
    },
  },
};
</script>
